import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../lib/firebase';
import {
  LogIn,
  LogOut,
  Image as ImageIcon,
  FileText,
  MessageSquare,
  BarChart3,
  Eye,
  EyeOff,
  Layout,
  TrendingUp,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useFirestoreCollection } from '../hooks/useFirestoreCollection';
import type { ContactRequest } from '../types/firebase';

import DashboardTab from '../components/Admin/DashboardTab';
import MediaTab from '../components/Admin/MediaTab';
import ContentTab from '../components/Admin/ContentTab';
import GalleryTab from '../components/Admin/GalleryTab';
import RequestsTab from '../components/Admin/RequestsTab';
import AnalyticsTab from '../components/Admin/AnalyticsTab';

const AdminPage: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');

  // Realtime count of new requests for badge
  const { data: allRequests } = useFirestoreCollection<ContactRequest>({
    collection: 'requests',
    realtime: true,
  });
  const newRequestCount = allRequests.filter((r) => r.status === 'new').length;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      toast.success('Connexion reussie');
      setEmail('');
      setPassword('');
    } catch (error: any) {
      toast.error('Erreur de connexion. Verifiez vos identifiants.');
      console.error('Login error:', error);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      toast.success('Deconnexion reussie');
      setActiveTab('dashboard');
    } catch {
      toast.error('Erreur lors de la deconnexion');
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Tableau de bord', icon: BarChart3, badge: 0 },
    { id: 'media', label: 'Mediatheque', icon: ImageIcon, badge: 0 },
    { id: 'content', label: 'Contenu', icon: FileText, badge: 0 },
    { id: 'gallery', label: 'Galerie', icon: Layout, badge: 0 },
    { id: 'requests', label: 'Demandes', icon: MessageSquare, badge: newRequestCount },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp, badge: 0 },
  ];

  // -- Loading spinner --
  if (loading) {
    return (
      <div className="pt-24 min-h-screen bg-gray-50 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  // -- Login form --
  if (!user) {
    return (
      <div className="pt-24 min-h-screen bg-gray-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-md w-full mx-4"
        >
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-red-600 to-red-700 p-8 text-center">
              <LogIn className="w-16 h-16 text-white mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">
                Administration Autocat
              </h2>
              <p className="text-red-100">
                Acces reserve aux administrateurs
              </p>
            </div>

            <form onSubmit={handleLogin} className="p-8 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none transition-colors"
                  placeholder="admin@autocat.lu"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mot de passe
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none transition-colors"
                    placeholder="--------"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <motion.button
                type="submit"
                disabled={loginLoading}
                whileHover={!loginLoading ? { scale: 1.02 } : {}}
                whileTap={!loginLoading ? { scale: 0.98 } : {}}
                className={`w-full flex items-center justify-center space-x-3 py-3 font-semibold rounded-lg transition-all duration-200 ${
                  loginLoading
                    ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                {loginLoading ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      className="w-5 h-5 border-2 border-gray-600 border-t-transparent rounded-full"
                    />
                    <span>Connexion...</span>
                  </>
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    <span>Se connecter</span>
                  </>
                )}
              </motion.button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  // -- Authenticated admin panel --
  return (
    <div className="pt-24 min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-24 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-xl font-bold text-gray-900">
              Administration Autocat
            </h1>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                {user.email}
              </span>
              <motion.button
                onClick={handleLogout}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Deconnexion</span>
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <div className="w-64 bg-white rounded-2xl shadow-lg h-fit sticky top-44">
            <div className="p-6">
              <nav className="space-y-2">
                {tabs.map((tab) => {
                  const IconComponent = tab.icon;
                  return (
                    <motion.button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      whileHover={{ x: 4 }}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-all duration-200 ${
                        activeTab === tab.id
                          ? 'bg-red-600 text-white shadow-lg'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <IconComponent className="w-5 h-5" />
                      <span className="font-medium flex-1">{tab.label}</span>
                      {tab.badge > 0 && (
                        <span
                          className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                            activeTab === tab.id
                              ? 'bg-white text-red-600'
                              : 'bg-red-600 text-white'
                          }`}
                        >
                          {tab.badge}
                        </span>
                      )}
                    </motion.button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1">
            {activeTab === 'dashboard' && <DashboardTab onNavigate={setActiveTab} />}
            {activeTab === 'media' && <MediaTab />}
            {activeTab === 'content' && <ContentTab />}
            {activeTab === 'gallery' && <GalleryTab />}
            {activeTab === 'requests' && <RequestsTab />}
            {activeTab === 'analytics' && <AnalyticsTab />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
